package br.com.texeratx.boardgametimeapp.core.active_match

import br.com.texeratx.boardgametimeapp.core.utils.currentTimeMillis
import br.com.texeratx.boardgametimeapp.data.model.expansion.ExpansionDTO
import br.com.texeratx.boardgametimeapp.data.model.game.GameDTO
import br.com.texeratx.boardgametimeapp.data.model.player.PlayerDTO
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlin.random.Random

class ActiveMatchController(
    private val storage: ActiveMatchStorage,
    private val clock: () -> Long = ::currentTimeMillis
) {

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val _state = MutableStateFlow<ActiveMatchState>(ActiveMatchState.Idle)
    val state: StateFlow<ActiveMatchState> = _state.asStateFlow()

    private val _lastFinishedDraft = MutableStateFlow<MatchDraft?>(null)
    val lastFinishedDraft: StateFlow<MatchDraft?> = _lastFinishedDraft.asStateFlow()


    private var currentMatch: ActiveMatch? = null
    private var status: ActiveMatchStatus? = null

    private var pausedAtEpochMillis: Long? = null
    private var accumulatedPausedMillis: Long = 0L

    private var lastEmittedSecond: Long = -1L
    private var tickerStarted: Boolean = false
    private var hasUserInteraction: Boolean = false

    init {
        // ✅ Ao criar o controller (app abriu), tenta restaurar do storage
        scope.launch {
            restoreFromStorage()
        }
    }

    fun startMatch(
        game: GameDTO,
        players: List<PlayerDTO>,
        expansions: List<ExpansionDTO>
    ) {
        hasUserInteraction = true
        val now = clock()
        val firstIndex = Random.nextInt(players.size) // players.size >= 1 sempre aqui

        currentMatch = ActiveMatch(
            gameId = game.id,
            gameName = game.name,
            gameImageUrl = game.imageUrl,

            playerIds = players.map { it.id },
            playerNames = players.map { it.name },
            playerImageUrls = players.map { it.imageUrl },

            expansionIds = expansions.map { it.id },
            expansionNames = expansions.map { it.name },
            startedAtEpochMillis = now,
            firstPlayerIndex = firstIndex,
        )

        status = ActiveMatchStatus.RUNNING
        pausedAtEpochMillis = null
        accumulatedPausedMillis = 0L
        lastEmittedSecond = -1L

        val match = currentMatch!!
        _state.value = ActiveMatchState.Running(
            match = match,
            elapsedMillis = 0L,
            formattedElapsed = formatElapsed(0L)
        )

        persist()
        ensureTicker()
    }

    fun pause() {
        hasUserInteraction = true
        val match = currentMatch ?: return
        if (status != ActiveMatchStatus.RUNNING) return

        val now = clock()
        pausedAtEpochMillis = now
        status = ActiveMatchStatus.PAUSED

        val elapsed = computeElapsed(now)
        _state.value = ActiveMatchState.Paused(
            match = match,
            elapsedMillis = elapsed,
            formattedElapsed = formatElapsed(elapsed)
        )

        persist()
    }

    fun resume() {
        hasUserInteraction = true
        val match = currentMatch ?: return
        if (status != ActiveMatchStatus.PAUSED) return

        val now = clock()
        val pausedAt = pausedAtEpochMillis ?: now

        accumulatedPausedMillis += (now - pausedAt).coerceAtLeast(0L)
        pausedAtEpochMillis = null
        status = ActiveMatchStatus.RUNNING

        val elapsed = computeElapsed(now)
        _state.value = ActiveMatchState.Running(
            match = match,
            elapsedMillis = elapsed,
            formattedElapsed = formatElapsed(elapsed)
        )

        persist()
        ensureTicker()
    }

    fun finish() {
        hasUserInteraction = true
        val match = currentMatch ?: return
        val now = clock()
        val totalElapsedMillis = computeElapsed(now)

        // ✅ cria draft (minutos arredondados para cima se tiver segundos)
        val minutes = ((totalElapsedMillis + 59_999L) / 60_000L).toInt().coerceAtLeast(0)

        val draft = MatchDraft(
            gameId = match.gameId,
            gameName = match.gameName,
            dateIso = br.com.texeratx.boardgametimeapp.ui.utils.epochMillisToApiDateTime(match.startedAtEpochMillis),
            durationMinutes = minutes,
            expansionIds = match.expansionIds,
            expansionNames = match.expansionNames,
            scores = match.playerIds.zip(match.playerNames).map { (id, name) ->
                PlayerScoreDraft(playerId = id, playerName = name, pointsText = "")
            },
            observation = null,

            gameImageUrl = match.gameImageUrl,
            playerImageUrls = match.playerImageUrls
        )

        _lastFinishedDraft.value = draft

        status = ActiveMatchStatus.FINISHED
        clearInternal()

        // ✅ não restaurar depois de matar app
        scope.launch { storage.clear() }

        // ✅ encerrou: não é mais "partida ativa", então some o miniPlayer
        _state.value = ActiveMatchState.Idle
    }

    fun clear() {
        hasUserInteraction = true
        _state.value = ActiveMatchState.Idle
        clearInternal()

        scope.launch { storage.clear() }
    }

    private fun clearInternal() {
        currentMatch = null
        status = null
        pausedAtEpochMillis = null
        accumulatedPausedMillis = 0L
        lastEmittedSecond = -1L
    }

    private fun ensureTicker() {
        if (tickerStarted) return
        tickerStarted = true

        scope.launch {
            while (isActive) {
                val match = currentMatch
                val st = status

                if (match == null || st == null) {
                    delay(300L)
                    continue
                }

                if (st == ActiveMatchStatus.RUNNING) {
                    val now = clock()
                    val elapsed = computeElapsed(now)

                    val currentSecond = elapsed / 1000L
                    if (currentSecond != lastEmittedSecond) {
                        if (status != ActiveMatchStatus.RUNNING || currentMatch !== match) {
                            delay(200L)
                            continue
                        }
                        lastEmittedSecond = currentSecond
                        _state.value = ActiveMatchState.Running(
                            match = match,
                            elapsedMillis = elapsed,
                            formattedElapsed = formatElapsed(elapsed)
                        )
                    }
                }

                delay(200L)
            }
        }
    }

    private fun computeElapsed(nowEpochMillis: Long): Long {
        val match = currentMatch ?: return 0L
        val endMillis = pausedAtEpochMillis ?: nowEpochMillis
        val elapsed = endMillis - match.startedAtEpochMillis - accumulatedPausedMillis
        return elapsed.coerceAtLeast(0L)
    }

    private fun formatElapsed(elapsedMillis: Long): String {
        val totalSeconds = (elapsedMillis / 1000L).coerceAtLeast(0L)
        val minutes = totalSeconds / 60L
        val seconds = totalSeconds % 60L

        val m = minutes.toString().padStart(3, '0')
        val s = seconds.toString().padStart(2, '0')
        return "$m:$s"
    }

    private fun persist() {
        val match = currentMatch ?: return
        val st = status ?: return

        val snapshot = ActiveMatchSnapshot(
            match = match,
            status = st,
            pausedAtEpochMillis = pausedAtEpochMillis,
            accumulatedPausedMillis = accumulatedPausedMillis
        )

        scope.launch {
            storage.save(snapshot)
        }
    }

    fun hasActiveMatch(): Boolean {
        return state.value !is ActiveMatchState.Idle
    }

    /**
     * Se você quiser tratar "Finished" como não-ativo, use este:
     */
    fun hasRunningOrPausedMatch(): Boolean {
        return when (state.value) {
            is ActiveMatchState.Running,
            is ActiveMatchState.Paused -> true
            else -> false
        }
    }

    private suspend fun restoreFromStorage() {
        if (hasUserInteraction) return
        val snapshot = storage.load() ?: return
        if (hasUserInteraction) return

        currentMatch = snapshot.match
        status = snapshot.status
        pausedAtEpochMillis = snapshot.pausedAtEpochMillis
        accumulatedPausedMillis = snapshot.accumulatedPausedMillis
        lastEmittedSecond = -1L

        val match = snapshot.match

        when (snapshot.status) {
            ActiveMatchStatus.RUNNING -> {
                val now = clock()
                val elapsed = computeElapsed(now)
                _state.value = ActiveMatchState.Running(
                    match = match,
                    elapsedMillis = elapsed,
                    formattedElapsed = formatElapsed(elapsed)
                )
                ensureTicker()
            }

            ActiveMatchStatus.PAUSED -> {
                val now = clock()
                val elapsed = computeElapsed(now)
                _state.value = ActiveMatchState.Paused(
                    match = match,
                    elapsedMillis = elapsed,
                    formattedElapsed = formatElapsed(elapsed)
                )
            }

            ActiveMatchStatus.FINISHED -> {
                // Se cair aqui (não deveria, pois limpamos no finish), só limpa por segurança
                storage.clear()
                _state.value = ActiveMatchState.Idle
                clearInternal()
            }
        }
    }

    fun clearLastFinishedDraft() {
        _lastFinishedDraft.value = null
    }

}
